import { ChangeDetectorRef, Component, ElementRef, Inject, Input, ViewChild, HostListener, LOCALE_ID, NgZone, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { ChartData, ChartOption, ChartConfig }                                          from './chart';
import { ChartHelper }                                                                  from './chart-helper';

export enum CsChartType {
	donut  = 'donut',
	bar    = 'bar',
	line   = 'line',
	area   = 'area',
	pie       = 'pie',
	column    = 'column',
	waterfall = 'waterfall',
	treemap   = 'treemap',
	funnel    = 'funnel',
	scatter   = 'scatter',
	bubble    = 'bubble',
	gauge     = 'gauge',
	/** Rendered as HTML by the chat (KPI tiles, not an SVG plot) — cs-chart ignores it. */
	card          = 'card',
	decomposition = 'decomposition'
}

/**
 * Chart plotter.
 * Supports line , area , pie , donut & bar types.
 */
@Component({
	standalone: false,
	selector:    'cs-chart',
	templateUrl: './chart.component.html'

})
export class CsChartComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('container') container: ElementRef;
	@ViewChild('svg') svgRef: ElementRef;
	private resizeObserver?: ResizeObserver;
	private lastWidth  = 0;
	private lastHeight = 0;
	/**
	 * The type of chart.
	 */
	@Input() type: CsChartType;
	/**
	 * Height of chart (px)
	 */
	@Input() height = 500;
	/**
	 * The Data to plot.
	 */
	@Input() data: ChartData;
	/**
	 * Title to add to the chart.
	 */
	@Input() title: string;
	/**
	 * Option for plotting the chart.
	 */
	@Input() options: ChartOption;
	chartConfig: ChartConfig;
	private showAxis: boolean;
	private axisConfig;

	constructor(
		private readonly ngZone: NgZone,
		private readonly cdr: ChangeDetectorRef,
		@Inject(LOCALE_ID) private readonly locale: string) {
		// All chart number formatting follows the app locale (LOCALE_ID), not a hardcoded one.
		ChartHelper.locale = locale;
	}

	ngOnInit() {
		this.initOptions();
	}

	ngAfterViewInit() {
		this.draw();
		// Deliberately do NOT seed lastWidth/lastHeight here: draw() above may have measured a
		// pre-layout 0 while a fresh measurement now would already see the final size — seeding
		// from it would make the observer's first callback look like "no change" and a chart
		// drawn at 0px would never repaint. Leaving 0/0 guarantees one redraw once real
		// dimensions exist, which is cheap and always correct.

		// Redraw whenever the container's width actually changes. This covers charts first laid out
		// at 0px — a transcript row re-opened from a saved session, or an off-screen row under
		// `content-visibility: auto` — which would otherwise never paint. The callback runs OUTSIDE
		// Angular's zone, so it must re-enter via NgZone or the child chart's [config] input never
		// updates and the redraw is invisible.
		if (typeof ResizeObserver !== 'undefined' && this.container?.nativeElement) {
			this.resizeObserver = new ResizeObserver(() => {
				const el = this.container?.nativeElement;
				const w  = el?.offsetWidth  ?? 0;
				const h  = el?.offsetHeight ?? 0;
				// Watch BOTH dimensions: under `content-visibility: auto` a re-opened row keeps its
				// width and only the height snaps from skipped/0 to real when it is finally laid out.
				if (w > 0 && h > 0 && (w !== this.lastWidth || h !== this.lastHeight)) {
					this.lastWidth  = w;
					this.lastHeight = h;
					this.ngZone.run(() => this.draw());
				}
			});
			this.resizeObserver.observe(this.container.nativeElement);
		}
	}

	ngOnDestroy() {
		this.resizeObserver?.disconnect();
	}

	@HostListener('window:resize', ['$event'])
	onResize    = (event) => {
		this.draw();
	};
	onMouseMove = (e) => {
	};

	/**
	 * Refresh chart.
	 */
	public Refresh() {
		setTimeout(_ => {
			this.initOptions();
			this.draw();
		}, 1);
	}

	private getOrDefault(val, def) {
		if (val == null) {
			return def;
		}
		return val;
	}

	private initOptions() {
		this.options = this.options || {};
		if (!this.options.ySteps || this.options.ySteps < 1) {
			this.options.ySteps = 5;
		}
		if (!this.options.colors) {
			this.options.colors = [
				'#0B486B', '#ECD078', '#D95B43', '#C02942', '#A8DBA8',
				'#542437', '#3B8686', '#F56991', '#45ADA8', '#79BD9A', '#53777A',
				'#594F4F', '#547980'];
		}
		this.options.xPadding        = this.getOrDefault(this.options.xPadding, 10);
		this.options.yPadding        = this.getOrDefault(this.options.yPadding, 10);
		this.options.titlePosition   = this.getOrDefault(this.options.titlePosition, 'top');
		this.options.showLegend      = this.getOrDefault(this.options.showLegend, true);
		this.options.animateIntro    = this.getOrDefault(this.options.animateIntro, true);
		this.options.animateHover    = this.getOrDefault(this.options.animateHover, true);
		this.options.isStacked       = this.getOrDefault(this.options.isStacked, false);
		this.options.drawPoints      = this.getOrDefault(this.options.drawPoints, true);
		this.options.fontFamily      = this.getOrDefault(this.options.fontFamily, 'Arial,Courier');
		this.options.yLabelFormatter = this.getOrDefault(this.options.yLabelFormatter, (label => label));
		this.chartConfig             = Object.assign({}, this.options);
		this.chartConfig.xPadding    = 4;
		this.chartConfig.yPadding    = 8;
		this.chartConfig.data        = this.data;
		this.chartConfig.type        = this.type;
	}

	private initDrawing() {
		const cs = window.getComputedStyle(this.container.nativeElement);

		this.chartConfig.chartWidth  = this.container.nativeElement.offsetWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
		this.chartConfig.chartHeight = this.container.nativeElement.offsetHeight - parseFloat(cs.paddingBottom) - parseFloat(cs.paddingTop);
		this.chartConfig             = Object.assign({}, this.chartConfig);
	}

	private draw() {
		if (!this.container || !this.container.nativeElement) {
			return;
		}
		this.initDrawing();
		// chartConfig was reassigned. Under an OnPush ancestor (e.g. a chat transcript row) the
		// next change-detection pass skips this subtree unless the ancestor path is marked, so the
		// child bar/line/pie component would never see the new [config]. Mark the path explicitly.
		this.cdr.markForCheck();
	}
}
